import { ArrowLeft, Box, ExternalLink, Minus, Plus, RotateCcw } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type WheelEvent,
} from "react";
import { Link } from "react-router";

import dynamoDbLogoUrl from "./assets/amazon-dynamodb.svg";
import s3LogoUrl from "./assets/amazon-s3.svg";
import postgresLogoUrl from "./assets/postgresql.svg";
import styles from "./entity-map.module.css";

export type EntityMapSource = "postgres" | "dynamodb" | "s3";

export type EntityMapKind =
  | "table"
  | "view"
  | "materialised-view"
  | "materialized-view"
  | "function"
  | "procedure"
  | "dynamodb-table"
  | "s3-bucket"
  | (string & {});

export interface EntityMapPosition {
  x: number;
  y: number;
  z: number;
}

export interface EntityMapNode {
  id: string;
  label: string;
  name?: string;
  source: EntityMapSource;
  kind: EntityMapKind;
  schema?: string;
  namespace?: string;
  fields?: string[];
  position?: EntityMapPosition;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}

export interface EntityMapEdge {
  id?: string;
  source: string;
  target: string;
  label?: string;
  kind?: string;
  confidence?: string;
  metadata?: Record<string, unknown>;
}

export interface EntityMapProps {
  nodes: EntityMapNode[];
  edges: EntityMapEdge[];
  drilldown?: EntityMapDrilldown | null;
  onDrilldownChange?: (
    drilldown: EntityMapDrilldown | null,
    options?: { replace?: boolean },
  ) => void;
  selectedNodeId?: string | null;
  onSelectNode?: (node: EntityMapNode | null) => void;
  getNodeHref?: (node: EntityMapNode) => string;
  className?: string;
  emptyMessage?: string;
}

interface Size {
  width: number;
  height: number;
}

interface LayoutNode extends EntityMapNode {
  worldX: number;
  worldY: number;
  width: number;
  height: number;
}

interface ScreenNode extends LayoutNode {
  screenX: number;
  screenY: number;
  screenWidth: number;
  screenHeight: number;
}

export type EntityMapVisualKind =
  | "table"
  | "view"
  | "materialised-view"
  | "function"
  | "procedure"
  | "dynamodb-table"
  | "s3-bucket"
  | "entity";

type VisualKind = EntityMapVisualKind;

export type EntityMapDrilldown = {
  schema: string;
  kind?: EntityMapVisualKind;
  bucket?: string;
};

interface ConnectorGeometry {
  points: Array<{ x: number; y: number }>;
  labelX: number;
  labelY: number;
  arrowAngle: number;
}

type DiagramData = {
  nodes: EntityMapNode[];
  edges: EntityMapEdge[];
};

type DrilldownState = EntityMapDrilldown;

const LEAF_NODE_LIMIT = 28;
const ALPHA_BUCKETS = [
  { id: "a-f", label: "A–F", pattern: /^[a-f]/i },
  { id: "g-l", label: "G–L", pattern: /^[g-l]/i },
  { id: "m-r", label: "M–R", pattern: /^[m-r]/i },
  { id: "s-z", label: "S–Z", pattern: /^[s-z]/i },
  { id: "0-9", label: "0–9", pattern: /^[0-9]/ },
  { id: "other", label: "Other", pattern: /^[^a-z0-9]/i },
] as const;

const SOURCE_LABELS: Record<EntityMapSource, string> = {
  postgres: "PostgreSQL",
  dynamodb: "DynamoDB",
  s3: "Amazon S3",
};

const SOURCE_COLOURS: Record<EntityMapSource, string> = {
  postgres: "#4b8bbe",
  dynamodb: "#d35cda",
  s3: "#3baa72",
};
const SOURCE_LOGOS: Record<EntityMapSource, string> = {
  postgres: postgresLogoUrl,
  dynamodb: dynamoDbLogoUrl,
  s3: s3LogoUrl,
};

const SOURCE_ORDER: EntityMapSource[] = ["postgres", "dynamodb", "s3"];
const CARD_WIDTH = 132;
const CARD_HEIGHT = 122;
const COLUMN_GAP = 112;
const ROW_GAP = 82;
const SOURCE_GAP = 240;

const KIND_LABELS: Record<VisualKind, string> = {
  table: "Table",
  view: "View",
  "materialised-view": "Materialised view",
  function: "Function",
  procedure: "Procedure",
  "dynamodb-table": "DynamoDB table",
  "s3-bucket": "S3 bucket",
  entity: "Entity",
};

const KIND_GLYPHS: Record<VisualKind, string> = {
  table: "▦",
  view: "◉",
  "materialised-view": "◆",
  function: "ƒ",
  procedure: "▶",
  "dynamodb-table": "⬢",
  "s3-bucket": "▰",
  entity: "■",
};

const KIND_LEGEND: VisualKind[] = [
  "table",
  "view",
  "materialised-view",
  "function",
  "procedure",
  "dynamodb-table",
  "s3-bucket",
];

function visualKind(kind: string, source?: EntityMapSource): VisualKind {
  if (source === "dynamodb") return "dynamodb-table";
  if (source === "s3") return "s3-bucket";
  const normalised = kind.toLocaleLowerCase();
  if (normalised.includes("materialised") || normalised.includes("materialized")) {
    return "materialised-view";
  }
  if (normalised.includes("dynamodb")) return "dynamodb-table";
  if (normalised.includes("bucket")) return "s3-bucket";
  if (normalised.includes("procedure")) return "procedure";
  if (normalised.includes("function")) return "function";
  if (normalised.includes("view")) return "view";
  if (normalised.includes("table")) return "table";
  return "entity";
}

function overviewGraph(nodes: EntityMapNode[], edges: EntityMapEdge[]): DiagramData {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const endpoint = new Map<string, string>();
  const overviewNodes: EntityMapNode[] = [];
  const postgresGroups = new Map<string, EntityMapNode[]>();

  for (const node of nodes) {
    if (node.source !== "postgres") {
      endpoint.set(node.id, node.id);
      overviewNodes.push(node);
      continue;
    }
    const schema = node.schema ?? node.namespace ?? "public";
    postgresGroups.set(schema, [...(postgresGroups.get(schema) ?? []), node]);
    endpoint.set(node.id, `overview:postgres:${schema}`);
  }

  const internalLinks = new Map<string, number>();
  const groupedEdges = new Map<string, EntityMapEdge[]>();
  for (const edge of edges) {
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) continue;
    const source = endpoint.get(edge.source) ?? edge.source;
    const target = endpoint.get(edge.target) ?? edge.target;
    if (source === target) {
      internalLinks.set(source, (internalLinks.get(source) ?? 0) + 1);
      continue;
    }
    const key = `${source}::${target}::${edge.kind ?? "relationship"}`;
    groupedEdges.set(key, [...(groupedEdges.get(key) ?? []), edge]);
  }

  for (const [schema, schemaNodes] of postgresGroups) {
    const id = `overview:postgres:${schema}`;
    overviewNodes.push({
      id,
      source: "postgres",
      kind: "schema",
      label: schema,
      name: schema,
      schema,
      namespace: schema,
      metadata: {
        aggregate: true,
        aggregateCount: schemaNodes.length,
        drilldownLevel: "schema",
        drilldownSchema: schema,
        internalLinks: internalLinks.get(id) ?? 0,
      },
    });
  }

  const overviewEdges = Array.from(groupedEdges.entries(), ([key, group]) => {
    const first = group[0];
    const source = endpoint.get(first.source) ?? first.source;
    const target = endpoint.get(first.target) ?? first.target;
    const declared = first.kind === "foreign-key";
    return {
      ...first,
      id: `overview:${key}`,
      source,
      target,
      label:
        group.length === 1
          ? first.label
          : `${group.length} ${declared ? "foreign keys" : "inferred links"}`,
      metadata: group.length === 1 ? first.metadata : { relationshipCount: group.length },
    };
  });

  return { nodes: overviewNodes, edges: overviewEdges };
}

function nodeBucket(node: EntityMapNode) {
  return ALPHA_BUCKETS.find((bucket) => bucket.pattern.test(node.name ?? node.label)) ?? ALPHA_BUCKETS.at(-1)!;
}

function drilldownGraph(
  nodes: EntityMapNode[],
  edges: EntityMapEdge[],
  drilldown: DrilldownState,
): DiagramData {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const countInternalLinks = (members: EntityMapNode[]) => {
    const memberIds = new Set(members.map((node) => node.id));
    return edges.reduce(
      (count, edge) =>
        count + (memberIds.has(edge.source) && memberIds.has(edge.target) ? 1 : 0),
      0,
    );
  };
  const schemaNodes = nodes.filter(
    (node) =>
      node.source === "postgres" &&
      (node.schema ?? node.namespace ?? "public") === drilldown.schema,
  );
  const focusNodes = drilldown.kind
    ? schemaNodes.filter((node) => visualKind(node.kind, node.source) === drilldown.kind)
    : schemaNodes;
  const leafNodes = drilldown.bucket
    ? focusNodes.filter((node) => nodeBucket(node).id === drilldown.bucket)
    : focusNodes;
  const focusIds = new Set(leafNodes.map((node) => node.id));
  const endpoint = new Map<string, string>();
  const displayNodes = new Map<string, EntityMapNode>();

  if (!drilldown.kind) {
    const byKind = new Map<VisualKind, EntityMapNode[]>();
    for (const node of schemaNodes) {
      const kind = visualKind(node.kind, node.source);
      byKind.set(kind, [...(byKind.get(kind) ?? []), node]);
    }
    for (const [kind, kindNodes] of byKind) {
      const id = `drilldown:kind:${drilldown.schema}:${kind}`;
      displayNodes.set(id, {
        id,
        source: "postgres",
        kind,
        label: KIND_LABELS[kind],
        name: KIND_LABELS[kind],
        schema: drilldown.schema,
        namespace: drilldown.schema,
        metadata: {
          aggregate: true,
          aggregateCount: kindNodes.length,
          internalLinks: countInternalLinks(kindNodes),
          drilldownKind: kind,
          drilldownLevel: "kind",
          drilldownSchema: drilldown.schema,
        },
      });
      for (const node of kindNodes) endpoint.set(node.id, id);
    }
  } else if (!drilldown.bucket && LEAF_NODE_LIMIT < focusNodes.length) {
    const byBucket = new Map<string, EntityMapNode[]>();
    for (const node of focusNodes) {
      const bucket = nodeBucket(node);
      byBucket.set(bucket.id, [...(byBucket.get(bucket.id) ?? []), node]);
    }
    for (const bucket of ALPHA_BUCKETS) {
      const bucketNodes = byBucket.get(bucket.id) ?? [];
      if (bucketNodes.length === 0) continue;
      const id = `drilldown:bucket:${drilldown.schema}:${drilldown.kind}:${bucket.id}`;
      displayNodes.set(id, {
        id,
        source: "postgres",
        kind: drilldown.kind,
        label: bucket.label,
        name: bucket.label,
        schema: drilldown.schema,
        namespace: drilldown.schema,
        metadata: {
          aggregate: true,
          aggregateCount: bucketNodes.length,
          internalLinks: countInternalLinks(bucketNodes),
          drilldownBucket: bucket.id,
          drilldownKind: drilldown.kind,
          drilldownLevel: "bucket",
          drilldownSchema: drilldown.schema,
        },
      });
      for (const node of bucketNodes) endpoint.set(node.id, id);
    }
  } else {
    for (const node of leafNodes) {
      endpoint.set(node.id, node.id);
      displayNodes.set(node.id, node);
    }
  }

  const externalNode = (node: EntityMapNode) => {
    if (node.source !== "postgres") return node;
    const schema = node.schema ?? node.namespace ?? "public";
    const id = `overview:postgres:${schema}`;
    const externalSchemaNodes = nodes.filter(
      (candidate) =>
        candidate.source === "postgres" &&
        (candidate.schema ?? candidate.namespace ?? "public") === schema,
    );
    return {
      id,
      source: "postgres" as const,
      kind: "schema",
      label: schema,
      name: schema,
      schema,
      namespace: schema,
      metadata: {
        aggregate: true,
        aggregateCount: externalSchemaNodes.length,
        internalLinks: countInternalLinks(externalSchemaNodes),
        drilldownLevel: "schema",
        drilldownSchema: schema,
      },
    };
  };

  const groupedEdges = new Map<string, EntityMapEdge[]>();
  for (const edge of edges) {
    if (!focusIds.has(edge.source) && !focusIds.has(edge.target)) continue;
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode) continue;
    const sourceDisplay = endpoint.get(edge.source) ?? externalNode(sourceNode).id;
    const targetDisplay = endpoint.get(edge.target) ?? externalNode(targetNode).id;
    if (!displayNodes.has(sourceDisplay)) displayNodes.set(sourceDisplay, externalNode(sourceNode));
    if (!displayNodes.has(targetDisplay)) displayNodes.set(targetDisplay, externalNode(targetNode));
    if (sourceDisplay === targetDisplay) continue;
    const key = `${sourceDisplay}::${targetDisplay}::${edge.kind ?? "relationship"}`;
    groupedEdges.set(key, [...(groupedEdges.get(key) ?? []), edge]);
  }

  const diagramEdges = Array.from(groupedEdges.entries(), ([key, group]) => {
    const first = group[0]!;
    const sourceNode = nodeById.get(first.source)!;
    const targetNode = nodeById.get(first.target)!;
    const source = endpoint.get(first.source) ?? externalNode(sourceNode).id;
    const target = endpoint.get(first.target) ?? externalNode(targetNode).id;
    return {
      ...first,
      id: `drilldown:${key}`,
      source,
      target,
      label:
        group.length === 1
          ? first.label
          : `${group.length} ${first.kind === "foreign-key" ? "foreign keys" : "inferred links"}`,
      metadata: group.length === 1 ? first.metadata : { relationshipCount: group.length },
    };
  });

  return { nodes: Array.from(displayNodes.values()), edges: diagramEdges };
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function nodeHeight(_node: EntityMapNode) {
  return CARD_HEIGHT;
}

function useSourceLogos() {
  const [logos, setLogos] = useState<Partial<Record<EntityMapSource, HTMLImageElement>>>({});
  useEffect(() => {
    let cancelled = false;
    for (const source of SOURCE_ORDER) {
      const image = new Image();
      image.onload = () => {
        if (!cancelled) setLogos((current) => ({ ...current, [source]: image }));
      };
      image.src = SOURCE_LOGOS[source];
    }
    return () => {
      cancelled = true;
    };
  }, []);
  return logos;
}

function createLayout(
  nodes: EntityMapNode[],
  edges: EntityMapEdge[],
  overview = false,
): LayoutNode[] {
  const degree = new Map<string, number>();
  for (const edge of edges) {
    degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1);
    degree.set(edge.target, (degree.get(edge.target) ?? 0) + 1);
  }

  const bySource = new Map<EntityMapSource, EntityMapNode[]>();
  for (const source of SOURCE_ORDER) bySource.set(source, []);
  for (const node of nodes) bySource.get(node.source)?.push(node);

  for (const sourceNodes of bySource.values()) {
    sourceNodes.sort((left, right) => {
      const namespaceDifference = (left.schema ?? left.namespace ?? "").localeCompare(
        right.schema ?? right.namespace ?? "",
      );
      if (namespaceDifference !== 0) return namespaceDifference;
      const degreeDifference = (degree.get(right.id) ?? 0) - (degree.get(left.id) ?? 0);
      return degreeDifference || left.label.localeCompare(right.label);
    });
  }

  const layout: LayoutNode[] = [];
  const cardWidth = overview ? 108 : CARD_WIDTH;
  const cardHeight = overview ? 104 : CARD_HEIGHT;
  const columnGap = overview ? 20 : COLUMN_GAP;
  const rowGap = overview ? 32 : ROW_GAP;
  const sourceGap = overview ? 60 : SOURCE_GAP;
  let sourceStartX = 0;
  for (const source of SOURCE_ORDER) {
    const sourceNodes = bySource.get(source) ?? [];
    if (sourceNodes.length === 0) continue;
    const rowsPerColumn = overview
      ? Math.min(6, sourceNodes.length)
      : Math.max(3, Math.ceil(Math.sqrt(sourceNodes.length * 1.35)));
    const columnCount = Math.ceil(sourceNodes.length / rowsPerColumn);
    for (let index = 0; index < sourceNodes.length; index += 1) {
      const node = sourceNodes[index];
      const column = Math.floor(index / rowsPerColumn);
      const row = index % rowsPerColumn;
      const height = nodeHeight(node);
      layout.push({
        ...node,
        width: cardWidth,
        height: overview ? cardHeight : height,
        worldX: sourceStartX + column * (cardWidth + columnGap),
        worldY: row * (cardHeight + rowGap),
      });
    }
    sourceStartX +=
      columnCount * cardWidth + Math.max(0, columnCount - 1) * columnGap + sourceGap;
  }
  return layout;
}

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Size>({ width: 960, height: 620 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => {
      const bounds = element.getBoundingClientRect();
      setSize({ width: Math.max(1, bounds.width), height: Math.max(1, bounds.height) });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}

function cssValue(stylesDeclaration: CSSStyleDeclaration, property: string, fallback: string) {
  return stylesDeclaration.getPropertyValue(property).trim() || fallback;
}

function roundedRectangle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function truncateLabel(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
  if (context.measureText(text).width <= maxWidth) return text;
  let result = text;
  while (result.length > 3 && context.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function pointerCoordinates(element: HTMLElement, clientX: number, clientY: number) {
  const bounds = element.getBoundingClientRect();
  return { x: clientX - bounds.left, y: clientY - bounds.top };
}

function controlButtonLabel(action: "in" | "out" | "reset") {
  if (action === "in") return "Zoom in";
  if (action === "out") return "Zoom out";
  return "Reset view";
}

function edgeLabel(edge: EntityMapEdge, inferred: boolean) {
  if (inferred) return edge.label ? `inferred · ${edge.label}` : "inferred relationship";
  const sourceColumns = Array.isArray(edge.metadata?.sourceColumns)
    ? edge.metadata.sourceColumns.filter((value): value is string => typeof value === "string")
    : [];
  const targetColumns = Array.isArray(edge.metadata?.targetColumns)
    ? edge.metadata.targetColumns.filter((value): value is string => typeof value === "string")
    : [];
  const columnPairs = sourceColumns
    .map((sourceColumn, index) =>
      targetColumns[index] ? `${sourceColumn} → ${targetColumns[index]}` : sourceColumn,
    )
    .join(", ");
  if (columnPairs && edge.label) return `${columnPairs} · ${edge.label}`;
  return columnPairs || (edge.label ? `FK · ${edge.label}` : "Foreign key");
}

function connectorGeometry(source: ScreenNode, target: ScreenNode, offset: number): ConnectorGeometry {
  const sourceCentreX = source.screenX + source.screenWidth / 2;
  const sourceCentreY = source.screenY + source.screenHeight / 2;
  const targetCentreX = target.screenX + target.screenWidth / 2;
  const targetCentreY = target.screenY + target.screenHeight / 2;
  const deltaX = targetCentreX - sourceCentreX;
  const deltaY = targetCentreY - sourceCentreY;
  const horizontal = source.source !== target.source || Math.abs(deltaX) >= Math.abs(deltaY);

  if (horizontal) {
    const direction = 0 <= deltaX ? 1 : -1;
    const start = {
      x: sourceCentreX + direction * source.screenWidth / 2,
      y: sourceCentreY,
    };
    const end = {
      x: targetCentreX - direction * target.screenWidth / 2,
      y: targetCentreY,
    };
    const middleX = (start.x + end.x) / 2 + offset;
    return {
      points: [start, { x: middleX, y: start.y }, { x: middleX, y: end.y }, end],
      labelX: middleX,
      labelY: (start.y + end.y) / 2,
      arrowAngle: direction === 1 ? 0 : Math.PI,
    };
  }

  const direction = 0 <= deltaY ? 1 : -1;
  const start = {
    x: sourceCentreX,
    y: sourceCentreY + direction * source.screenHeight / 2,
  };
  const end = {
    x: targetCentreX,
    y: targetCentreY - direction * target.screenHeight / 2,
  };
  const middleY = (start.y + end.y) / 2 + offset;
  return {
    points: [start, { x: start.x, y: middleY }, { x: end.x, y: middleY }, end],
    labelX: (start.x + end.x) / 2,
    labelY: middleY,
    arrowAngle: direction === 1 ? Math.PI / 2 : -Math.PI / 2,
  };
}

function drawArrow(
  context: CanvasRenderingContext2D,
  point: { x: number; y: number },
  angle: number,
  colour: string,
  size: number,
) {
  context.fillStyle = colour;
  context.beginPath();
  context.moveTo(point.x, point.y);
  context.lineTo(
    point.x - Math.cos(angle - Math.PI / 6) * size,
    point.y - Math.sin(angle - Math.PI / 6) * size,
  );
  context.lineTo(
    point.x - Math.cos(angle + Math.PI / 6) * size,
    point.y - Math.sin(angle + Math.PI / 6) * size,
  );
  context.closePath();
  context.fill();
}

function drawEntityCard(
  context: CanvasRenderingContext2D,
  node: ScreenNode,
  scale: number,
  logo: HTMLImageElement | undefined,
  cardColour: string,
  cardSecondaryColour: string,
  inkColour: string,
  mutedColour: string,
  borderColour: string,
  active: boolean,
  dimmed: boolean,
) {
  const kind = visualKind(node.kind, node.source);
  const sourceColour = SOURCE_COLOURS[node.source];
  const radius = Math.max(3, Math.min(7, 6 * scale));
  context.save();
  context.globalAlpha = dimmed ? 0.2 : 1;
  roundedRectangle(
    context,
    node.screenX,
    node.screenY,
    node.screenWidth,
    node.screenHeight,
    radius,
  );
  context.fillStyle = cardColour;
  context.fill();
  context.strokeStyle = active ? sourceColour : borderColour;
  context.lineWidth = active ? 2.2 : 1.1;
  context.stroke();

  const accentHeight = Math.max(3, 5 * scale);
  context.fillStyle = sourceColour;
  context.fillRect(
    node.screenX + radius,
    node.screenY,
    Math.max(0, node.screenWidth - radius * 2),
    accentHeight,
  );

  if (node.screenWidth < 42) {
    context.fillStyle = sourceColour;
    context.beginPath();
    context.arc(
      node.screenX + node.screenWidth / 2,
      node.screenY + node.screenHeight / 2,
      Math.max(3, Math.min(node.screenWidth, node.screenHeight) * 0.18),
      0,
      Math.PI * 2,
    );
    context.fill();
    context.restore();
    return;
  }

  const iconSize = Math.min(54 * scale, node.screenWidth * 0.42, node.screenHeight * 0.43);
  const iconX = node.screenX + (node.screenWidth - iconSize) / 2;
  const iconY = node.screenY + Math.max(12 * scale, node.screenHeight * 0.12);
  if (logo?.complete && logo.naturalWidth > 0) {
    context.drawImage(logo, iconX, iconY, iconSize, iconSize);
  } else {
    context.fillStyle = sourceColour;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = `700 ${Math.max(12, iconSize * 0.62)}px Inter, system-ui, sans-serif`;
    context.fillText(KIND_GLYPHS[kind], node.screenX + node.screenWidth / 2, iconY + iconSize / 2);
  }

  const badgeSize = Math.max(14, 20 * scale);
  const badgeX = node.screenX + node.screenWidth - badgeSize - Math.max(5, 7 * scale);
  const badgeY = node.screenY + Math.max(7, 9 * scale);
  roundedRectangle(context, badgeX, badgeY, badgeSize, badgeSize, Math.max(3, 4 * scale));
  context.fillStyle = cardSecondaryColour;
  context.fill();
  context.strokeStyle = borderColour;
  context.stroke();
  context.fillStyle = sourceColour;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `700 ${Math.max(8, 10 * scale)}px Inter, system-ui, sans-serif`;
  context.fillText(KIND_GLYPHS[kind], badgeX + badgeSize / 2, badgeY + badgeSize / 2 + 0.5);

  if (scale < 0.38) {
    context.restore();
    return;
  }

  const horizontalPadding = Math.max(6, 9 * scale);
  const titleY = node.screenY + node.screenHeight - Math.max(28, 31 * scale);
  context.fillStyle = inkColour;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `700 ${Math.min(12, Math.max(8, 11 * scale))}px Inter, system-ui, sans-serif`;
  context.fillText(
    truncateLabel(context, node.label, node.screenWidth - horizontalPadding * 2),
    node.screenX + node.screenWidth / 2,
    titleY,
  );

  const aggregateCount = node.metadata?.aggregateCount;
  const internalLinks = node.metadata?.internalLinks;
  const namespace = node.schema ?? node.namespace ?? KIND_LABELS[kind];
  const subtitle =
    typeof aggregateCount === "number"
      ? `${aggregateCount} resources · ${typeof internalLinks === "number" ? internalLinks : 0} internal links`
      : `${namespace} · ${KIND_LABELS[kind]}`;
  context.fillStyle = mutedColour;
  context.font = `500 ${Math.min(10, Math.max(7, 8.5 * scale))}px Inter, system-ui, sans-serif`;
  context.fillText(
    truncateLabel(context, subtitle, node.screenWidth - horizontalPadding * 2),
    node.screenX + node.screenWidth / 2,
    titleY + Math.max(13, 15 * scale),
  );
  context.restore();
}

function drawCluster(
  context: CanvasRenderingContext2D,
  nodes: ScreenNode[],
  label: string,
  colour: string,
  scale: number,
  nested = false,
) {
  if (nodes.length === 0) return;
  const horizontalPadding = Math.max(nested ? 10 : 16, (nested ? 15 : 24) * scale);
  const topPadding = Math.max(nested ? 18 : 25, (nested ? 25 : 36) * scale);
  const bottomPadding = Math.max(10, 18 * scale);
  const left = Math.min(...nodes.map((node) => node.screenX)) - horizontalPadding;
  const top = Math.min(...nodes.map((node) => node.screenY)) - topPadding;
  const right = Math.max(...nodes.map((node) => node.screenX + node.screenWidth)) + horizontalPadding;
  const bottom = Math.max(...nodes.map((node) => node.screenY + node.screenHeight)) + bottomPadding;

  context.save();
  roundedRectangle(context, left, top, right - left, bottom - top, nested ? 6 : 9);
  context.fillStyle = `${colour}${nested ? "08" : "0d"}`;
  context.fill();
  context.strokeStyle = `${colour}${nested ? "70" : "a8"}`;
  context.lineWidth = nested ? 0.8 : 1.2;
  context.setLineDash(nested ? [4, 4] : []);
  context.stroke();
  context.setLineDash([]);
  context.fillStyle = colour;
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.font = `${nested ? 600 : 700} ${nested ? 9 : 10.5}px Inter, system-ui, sans-serif`;
  context.fillText(label, left + 9, top + (nested ? 10 : 12));
  context.restore();
}

export function EntityMap({
  nodes,
  edges,
  drilldown: controlledDrilldown,
  onDrilldownChange,
  selectedNodeId,
  onSelectNode,
  getNodeHref,
  className,
  emptyMessage = "No entities to map yet",
}: EntityMapProps) {
  const [viewportRef, size] = useElementSize<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sourceLogos = useSourceLogos();
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [internalDrilldown, setInternalDrilldown] = useState<DrilldownState | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const drag = useRef({ pointerId: -1, x: 0, y: 0, moved: false });
  const pendingPan = useRef({ x: 0, y: 0 });
  const panFrame = useRef<number | null>(null);
  const drilldown = controlledDrilldown === undefined ? internalDrilldown : controlledDrilldown;
  const activeId = selectedNodeId === undefined ? internalSelectedId : selectedNodeId;

  const updateDrilldown = useCallback(
    (nextDrilldown: DrilldownState | null, options?: { replace?: boolean }) => {
      if (controlledDrilldown === undefined) setInternalDrilldown(nextDrilldown);
      onDrilldownChange?.(nextDrilldown, options);
    },
    [controlledDrilldown, onDrilldownChange],
  );

  useEffect(() => {
    setInternalSelectedId(null);
    setHoveredId(null);
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, [drilldown]);

  useEffect(() => {
    if (!drilldown) return;
    const schemaNodes = nodes.filter(
      (node) =>
        node.source === "postgres" &&
        (node.schema ?? node.namespace ?? "public") === drilldown.schema,
    );
    let nextDrilldown: DrilldownState | null = drilldown;
    if (schemaNodes.length === 0) {
      nextDrilldown = null;
    } else if (drilldown.kind) {
      const kindNodes = schemaNodes.filter(
        (node) => visualKind(node.kind, node.source) === drilldown.kind,
      );
      if (kindNodes.length === 0) {
        nextDrilldown = { schema: drilldown.schema };
      } else if (
        drilldown.bucket &&
        !kindNodes.some((node) => nodeBucket(node).id === drilldown.bucket)
      ) {
        nextDrilldown = { schema: drilldown.schema, kind: drilldown.kind };
      }
    }
    if (nextDrilldown === drilldown) return;
    updateDrilldown(nextDrilldown, { replace: true });
    setInternalSelectedId(null);
    setHoveredId(null);
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, [drilldown, nodes, updateDrilldown]);

  const diagram = useMemo(
    () => drilldown ? drilldownGraph(nodes, edges, drilldown) : overviewGraph(nodes, edges),
    [drilldown, edges, nodes],
  );

  const layoutNodes = useMemo(
    () => createLayout(diagram.nodes, diagram.edges, true),
    [diagram],
  );
  const layoutBounds = useMemo(() => {
    if (layoutNodes.length === 0) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    return {
      minX: Math.min(...layoutNodes.map((node) => node.worldX)),
      minY: Math.min(...layoutNodes.map((node) => node.worldY)),
      maxX: Math.max(...layoutNodes.map((node) => node.worldX + node.width)),
      maxY: Math.max(...layoutNodes.map((node) => node.worldY + node.height)),
    };
  }, [layoutNodes]);

  const fitScale = useMemo(() => {
    const layoutWidth = Math.max(1, layoutBounds.maxX - layoutBounds.minX);
    const layoutHeight = Math.max(1, layoutBounds.maxY - layoutBounds.minY);
    return Math.min(1, (size.width - 90) / layoutWidth, (size.height - 150) / layoutHeight);
  }, [layoutBounds, size]);
  const displayScale = fitScale * zoom;

  const screenNodes = useMemo<ScreenNode[]>(() => {
    const layoutCentreX = (layoutBounds.minX + layoutBounds.maxX) / 2;
    const layoutCentreY = (layoutBounds.minY + layoutBounds.maxY) / 2;
    const overviewOffsetY = 48;
    return layoutNodes.map((node) => ({
      ...node,
      screenX: size.width / 2 + (node.worldX - layoutCentreX) * displayScale + pan.x,
      screenY:
        size.height / 2 +
        (node.worldY - layoutCentreY) * displayScale +
        pan.y +
        overviewOffsetY,
      screenWidth: node.width * displayScale,
      screenHeight: node.height * displayScale,
    }));
  }, [displayScale, layoutBounds, layoutNodes, pan, size]);

  const nodeById = useMemo(() => new Map(screenNodes.map((node) => [node.id, node])), [screenNodes]);

  const connectedIds = useMemo(() => {
    const focusId = hoveredId ?? activeId;
    if (!focusId) return null;
    const ids = new Set([focusId]);
    for (const edge of diagram.edges) {
      if (edge.source === focusId) ids.add(edge.target);
      if (edge.target === focusId) ids.add(edge.source);
    }
    return ids;
  }, [activeId, diagram.edges, hoveredId]);

  const selectedNode = activeId ? nodeById.get(activeId) ?? null : null;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
    const pixelWidth = Math.max(1, Math.round(size.width * pixelRatio));
    const pixelHeight = Math.max(1, Math.round(size.height * pixelRatio));
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, size.width, size.height);

    const computedStyles = getComputedStyle(canvas);
    const cardColour = cssValue(computedStyles, "--nzr-card", "#ffffff");
    const cardSecondaryColour = cssValue(computedStyles, "--card-2", "#f7f8fa");
    const inkColour = cssValue(computedStyles, "--ink", "#17202a");
    const mutedColour = cssValue(computedStyles, "--nzr-muted", "#697386");
    const borderColour = cssValue(computedStyles, "--nzr-border", "#d6dae1");
    const focusId = hoveredId ?? activeId;
    const labelAllEdges = false;

    context.lineJoin = "round";
    context.lineCap = "round";
    for (const source of SOURCE_ORDER) {
      const sourceNodes = screenNodes.filter((node) => node.source === source);
      drawCluster(
        context,
        sourceNodes,
        SOURCE_LABELS[source],
        SOURCE_COLOURS[source],
        displayScale,
      );
    }
    for (const edge of diagram.edges) {
      const source = nodeById.get(edge.source);
      const target = nodeById.get(edge.target);
      if (!source || !target) continue;
      const focused = focusId === edge.source || focusId === edge.target;
      const unrelated = connectedIds !== null && !focused;
      const inferred = edge.kind?.toLocaleLowerCase().includes("inferred") ?? false;
      const edgeKey = edge.id ?? `${edge.source}:${edge.target}:${edge.label ?? ""}`;
      const offset = ((hash(edgeKey) % 7) - 3) * Math.max(3, 5 * displayScale);
      const geometry = connectorGeometry(source, target, offset);
      const edgeColour = focused ? SOURCE_COLOURS[source.source] : inkColour;

      context.globalAlpha = unrelated ? 0.08 : focused ? 0.98 : inferred ? 0.55 : 0.74;
      context.strokeStyle = edgeColour;
      context.lineWidth = focused ? 2.8 : inferred ? 1.7 : 2.1;
      context.setLineDash(inferred ? [7, 5] : []);
      context.beginPath();
      context.moveTo(geometry.points[0].x, geometry.points[0].y);
      for (let index = 1; index < geometry.points.length; index += 1) {
        context.lineTo(geometry.points[index].x, geometry.points[index].y);
      }
      context.stroke();
      context.setLineDash([]);
      drawArrow(
        context,
        geometry.points[geometry.points.length - 1],
        geometry.arrowAngle,
        edgeColour,
        focused ? 9 : 7,
      );

      if ((edge.label || edge.kind === "foreign-key") && (focused || labelAllEdges) && !unrelated) {
        context.globalAlpha = focused ? 1 : 0.94;
        context.font = `${focused ? 700 : 600} ${focused ? 10 : 9}px Inter, system-ui, sans-serif`;
        const label = truncateLabel(context, edgeLabel(edge, inferred), focused ? 230 : 170);
        const labelWidth = context.measureText(label).width + 12;
        roundedRectangle(
          context,
          geometry.labelX - labelWidth / 2,
          geometry.labelY - 9,
          labelWidth,
          18,
          4,
        );
        context.fillStyle = cardColour;
        context.fill();
        context.strokeStyle = inferred ? mutedColour : edgeColour;
        context.lineWidth = 0.9;
        context.setLineDash(inferred ? [3, 2] : []);
        context.stroke();
        context.setLineDash([]);
        context.fillStyle = inferred ? mutedColour : inkColour;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(label, geometry.labelX, geometry.labelY + 0.5);
      }
    }
    context.setLineDash([]);

    for (const node of screenNodes) {
      const active = activeId === node.id || hoveredId === node.id;
      const dimmed = connectedIds !== null && !connectedIds.has(node.id);
      drawEntityCard(
        context,
        node,
        displayScale,
        sourceLogos[node.source],
        cardColour,
        cardSecondaryColour,
        inkColour,
        mutedColour,
        borderColour,
        active,
        dimmed,
      );
    }
    context.globalAlpha = 1;
  }, [
    activeId,
    connectedIds,
    displayScale,
    diagram.edges,
    hoveredId,
    nodeById,
    screenNodes,
    size,
    sourceLogos,
  ]);

  useEffect(
    () => () => {
      if (panFrame.current !== null) cancelAnimationFrame(panFrame.current);
    },
    [],
  );

  const hitTest = useCallback(
    (x: number, y: number) => {
      for (let index = screenNodes.length - 1; index >= 0; index -= 1) {
        const node = screenNodes[index];
        const padding = Math.max(3, Math.min(10, 5 * displayScale));
        if (
          x >= node.screenX - padding &&
          x <= node.screenX + node.screenWidth + padding &&
          y >= node.screenY - padding &&
          y <= node.screenY + node.screenHeight + padding
        ) {
          return node;
        }
      }
      return null;
    },
    [displayScale, screenNodes],
  );

  const updateZoom = useCallback((delta: number) => {
    setZoom((current) => Math.min(5, Math.max(0.35, current + delta)));
  }, []);

  const resetView = useCallback(() => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const schedulePan = useCallback(() => {
    if (panFrame.current !== null) return;
    panFrame.current = requestAnimationFrame(() => {
      panFrame.current = null;
      const delta = pendingPan.current;
      pendingPan.current = { x: 0, y: 0 };
      setPan((current) => ({ x: current.x + delta.x, y: current.y + delta.y }));
    });
  }, []);

  const resetDiagramInteraction = (notifySelection = true) => {
    setInternalSelectedId(null);
    setHoveredId(null);
    setPan({ x: 0, y: 0 });
    setZoom(1);
    if (notifySelection) onSelectNode?.(null);
  };

  const expandNode = (node: ScreenNode) => {
    const level = node.metadata?.drilldownLevel;
    const schema = node.metadata?.drilldownSchema;
    if (typeof schema !== "string") return false;
    if (level === "schema") {
      updateDrilldown({ schema });
    } else if (level === "kind" && typeof node.metadata?.drilldownKind === "string") {
      updateDrilldown({ schema, kind: node.metadata.drilldownKind as VisualKind });
    } else if (
      level === "bucket" &&
      typeof node.metadata?.drilldownKind === "string" &&
      typeof node.metadata?.drilldownBucket === "string"
    ) {
      updateDrilldown({
        schema,
        kind: node.metadata.drilldownKind as VisualKind,
        bucket: node.metadata.drilldownBucket,
      });
    } else {
      return false;
    }
    resetDiagramInteraction(false);
    return true;
  };

  const stepBack = () => {
    if (!drilldown) return;
    if (drilldown.bucket) updateDrilldown({ schema: drilldown.schema, kind: drilldown.kind });
    else if (drilldown.kind) updateDrilldown({ schema: drilldown.schema });
    else updateDrilldown(null);
    resetDiagramInteraction(false);
  };

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.style.cursor = "grabbing";
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  };

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (drag.current.pointerId === event.pointerId) {
      const deltaX = event.clientX - drag.current.x;
      const deltaY = event.clientY - drag.current.y;
      if (Math.abs(deltaX) + Math.abs(deltaY) > 2) drag.current.moved = true;
      drag.current.x = event.clientX;
      drag.current.y = event.clientY;
      pendingPan.current.x += deltaX;
      pendingPan.current.y += deltaY;
      schedulePan();
      return;
    }
    const pointer = pointerCoordinates(event.currentTarget, event.clientX, event.clientY);
    const hit = hitTest(pointer.x, pointer.y);
    setHoveredId((current) => (current === hit?.id ? current : (hit?.id ?? null)));
    event.currentTarget.style.cursor = hit ? "pointer" : "grab";
  };

  const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    if (drag.current.pointerId !== event.pointerId) return;
    const moved = drag.current.moved || event.type === "pointercancel";
    drag.current.pointerId = -1;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const pointer = pointerCoordinates(event.currentTarget, event.clientX, event.clientY);
    const hit = hitTest(pointer.x, pointer.y);
    event.currentTarget.style.cursor = hit ? "pointer" : "grab";
    if (moved) return;
    if (hit && expandNode(hit)) return;
    const nextNode = hit && activeId !== hit.id ? hit : null;
    if (selectedNodeId === undefined) setInternalSelectedId(nextNode?.id ?? null);
    onSelectNode?.(nextNode);
  };

  const handlePointerLeave = () => {
    if (drag.current.pointerId === -1) setHoveredId(null);
  };

  const handleWheel = (event: WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    updateZoom(event.deltaY > 0 ? -0.12 : 0.12);
  };

  const handleKeyboard = (event: KeyboardEvent<HTMLCanvasElement>) => {
    const panStep = 30;
    if (event.key === "+" || event.key === "=") updateZoom(0.15);
    else if (event.key === "-") updateZoom(-0.15);
    else if (event.key === "0") resetView();
    else if (event.key === "ArrowLeft") setPan((current) => ({ ...current, x: current.x - panStep }));
    else if (event.key === "ArrowRight") setPan((current) => ({ ...current, x: current.x + panStep }));
    else if (event.key === "ArrowUp") setPan((current) => ({ ...current, y: current.y - panStep }));
    else if (event.key === "ArrowDown") setPan((current) => ({ ...current, y: current.y + panStep }));
    else return;
    event.preventDefault();
  };

  const drilldownLabel = drilldown
    ? [
        drilldown.schema,
        drilldown.kind ? KIND_LABELS[drilldown.kind] : null,
        drilldown.bucket
          ? ALPHA_BUCKETS.find((bucket) => bucket.id === drilldown.bucket)?.label
          : null,
      ].filter(Boolean).join(" / ")
    : "Schema overview";

  if (nodes.length === 0) {
    return (
      <div className={[styles.empty, className].filter(Boolean).join(" ")}>
        <Box size={24} aria-hidden="true" />
        <span>{emptyMessage}</span>
      </div>
    );
  }

  return (
    <section className={[styles.map, className].filter(Boolean).join(" ")} aria-label="Data architecture diagram">
      <div ref={viewportRef} className={styles.viewport}>
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          tabIndex={0}
          role="application"
          aria-label={`Interactive data architecture diagram showing ${diagram.nodes.length} nodes representing ${nodes.length} resources and ${edges.length} relationships. Drag to pan, scroll to zoom.`}
          onDoubleClick={resetView}
          onKeyDown={handleKeyboard}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onWheel={handleWheel}
        />
        <div className={styles.instructions}>
          {drilldownLabel} · Drag to pan · Scroll to zoom
        </div>
      </div>

      <div className={styles.controls} aria-label="Diagram controls">
        {drilldown && (
          <>
            <button type="button" onClick={stepBack} aria-label="Back one diagram level">
              <ArrowLeft size={15} aria-hidden="true" />
            </button>
            <span className={styles.drilldownLabel}>{drilldownLabel}</span>
          </>
        )}
        <button type="button" onClick={() => updateZoom(0.2)} aria-label={controlButtonLabel("in")}>
          <Plus size={15} aria-hidden="true" />
        </button>
        <button type="button" onClick={() => updateZoom(-0.2)} aria-label={controlButtonLabel("out")}>
          <Minus size={15} aria-hidden="true" />
        </button>
        <button type="button" onClick={resetView} aria-label={controlButtonLabel("reset")}>
          <RotateCcw size={14} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.legend} aria-label="Diagram legend">
        <div className={styles.sourceLegend}>
          {SOURCE_ORDER.map((source) => (
            <span key={source} className={styles.legendItem} data-source={source}>
              <span className={styles.legendSwatch} aria-hidden="true" />
              {SOURCE_LABELS[source]}
            </span>
          ))}
        </div>
        <div className={styles.relationLegend}>
          <span><i className={styles.declaredLine} aria-hidden="true" />Declared FK</span>
          <span><i className={styles.inferredLine} aria-hidden="true" />Inferred cross-store link</span>
        </div>
        <div className={styles.kindLegend}>
          {KIND_LEGEND.map((kind) => (
            <span key={kind} title={KIND_LABELS[kind]}>
              <b aria-hidden="true">{KIND_GLYPHS[kind]}</b>
              {KIND_LABELS[kind]}
            </span>
          ))}
        </div>
      </div>

      {selectedNode && (
        <aside className={styles.selection} aria-live="polite">
          <span className={styles.selectionGlyph} data-source={selectedNode.source} aria-hidden="true">
            {KIND_GLYPHS[visualKind(selectedNode.kind, selectedNode.source)]}
          </span>
          <span className={styles.selectionText}>
            <strong>{selectedNode.label}</strong>
            <span>
              {selectedNode.schema ?? selectedNode.namespace ?? SOURCE_LABELS[selectedNode.source]} ·{" "}
              {KIND_LABELS[visualKind(selectedNode.kind, selectedNode.source)]}
            </span>
          </span>
          <span className={styles.connectionCount}>
            {diagram.edges.filter(
              (edge) => edge.source === selectedNode.id || edge.target === selectedNode.id,
            ).length}{" "}
            links
          </span>
          {getNodeHref && selectedNode.metadata?.aggregate !== true && (
            <Link className={styles.selectionLink} to={getNodeHref(selectedNode)}>
              Open resource
              <ExternalLink size={12} aria-hidden="true" />
            </Link>
          )}
        </aside>
      )}
    </section>
  );
}
